import { ChunkEntry, Line } from '@atlassian/bitkit-diff';

import { DiffEntry } from '../types';

import { segmentize } from './segmenter';
import { ComparableRegionsOfChange, Region } from './tokenizers';
import {
  produceWordDiffRegions,
  produceWordDiffsFromRegions,
} from './word-differ';

/**
 * This is the lowest level we can build word diffs from because we
 * need all the lines to properly build and associate the word diffs
 * back to the original lines.
 * @param changes The collection of changes in a parsed file diff
 * @see diffparser
 */
export function addWordDiffToChunkLines(changes: Line[]): Line[] {
  const segments = segmentize(changes);
  const wordDiffRegions = produceWordDiffRegions(segments);
  const wordDiffs = produceWordDiffsFromRegions(segments, wordDiffRegions);

  const newChanges = [...changes];

  // This munged three collections together to update the data model,
  // original identified segments,
  // constructed word diffs,
  // and the original changes collection.
  wordDiffs.forEach((wordDiff, index) => {
    const { previousText, newText } = wordDiff;
    const matchingSegment = segments[index];
    const { startIndexOfPrevious, startIndexofNew } = matchingSegment;

    let charsSoFar = 0;
    previousText.split('\n').forEach((decoratedLine, idx) => {
      const currentLine = newChanges[startIndexOfPrevious! + idx];
      currentLine.wordDiff = `-${decoratedLine}`;
      currentLine.wordDiffRegions = regionsForLine(
        wordDiffRegions[index],
        currentLine,
        charsSoFar
      );
      charsSoFar += currentLine.content.length;
    });

    charsSoFar = 0;
    newText.split('\n').forEach((decoratedLine, idx) => {
      const currentLine = newChanges[startIndexofNew! + idx];
      currentLine.wordDiff = `+${decoratedLine}`;
      currentLine.wordDiffRegions = regionsForLine(
        wordDiffRegions[index],
        currentLine,
        charsSoFar
      );
      charsSoFar += currentLine.content.length;
    });
  });

  return newChanges;
}

function regionsForLine(
  comparableRegions: ComparableRegionsOfChange,
  line: Line,
  charsSoFar: number
): ComparableRegionsOfChange {
  function constrainRegionsToLine(regions: Region[]) {
    // The `regions` argument is going to contain a list of regions that spans
    // multiple lines. Our job in this function is to split those regions and
    // shift them so that they're relative to the start of each line rather than
    // the start of the chunk.
    //
    // Note that this is O(N^2) behavior; we're doing a linear scan of the
    // regions for each line of the chunk in addWordDiffToChunkLines. If we run
    // into performance issues, this could be a place to optimize. I didn't
    // optimize it because an O(n) version would be significantly less clear,
    // and the number of regions is unlikely to be huge.
    return (
      regions
        // First we normalize each region to be relative to the start of the line,
        // based on how many characters we've already seen in this chunk
        .map(region => ({
          ...region,
          start: Math.max(0, region.start - charsSoFar),
          end: Math.min(line.content.length - 1, region.end - charsSoFar),
        }))
        // Now we preserve only the regions that start within this line
        .filter(
          region => region.start >= 0 && region.start < line.content.length
        )
    );
  }
  return {
    originalRegions: constrainRegionsToLine(comparableRegions.originalRegions),
    revisedRegions: constrainRegionsToLine(comparableRegions.revisedRegions),
  };
}

/**
 * Utility to return an entirely new chunk with
 * any calculable word diffs attached at the line changes level.
 * @param chunk Parsed chunk portion of a parsed diff
 * @see diffparser
 */
export function addWordDiffToChunk(chunk: ChunkEntry): ChunkEntry {
  return {
    ...chunk,
    changes: addWordDiffToChunkLines(chunk.changes),
  };
}

/**
 * Utility to return an entirely new parsed diff with
 * any calculable word diffs attached at the line changes level.
 * @param diff Parsed diff
 * @see diffparser
 */
export function addWordDiffToDiff<T extends DiffEntry>(diff: T): T {
  return {
    ...diff,
    chunks: diff.chunks.map(addWordDiffToChunk),
  };
}

/**
 * Utility to return an entirely new collection of diffs with
 * any calculable word diffs attached at the line changes level.
 * @param diffs Parsed diffs
 * @see diffparser
 */
export function addWordDiffToDiffs<T extends DiffEntry>(diffs: T[]): T[] {
  return diffs.map(addWordDiffToDiff);
}
